import { isEmpty } from '@cck/common'
import { Spin, Layout } from 'antd'
import classNames from 'classnames'
import _ from 'lodash'
import React, { Suspense, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'

import {
  findIndexItemInner,
  indexItemsSelector,
  selectedIndexItemAtom,
  selectedOneDepthIndexItemAtom
} from 'atoms/index/Index'
import { selectedStandardIdAtom } from 'atoms/standard/Standard'
import NewBodyComponent from 'components/new_body/NewBodyComponent'
import NewLnbComponent from 'components/new_lnb/NewLnbComponent'
import NewSearchKeywordCountListComponent, {
  SearchKeywordCountItemAreaType
} from 'components/new_search/NewSearchKeywordCountListComponent'
import { findFirstChildIndexItem } from 'components/util/IndexItemUtil'
import { getIndex } from 'services/standard_client'

const { Sider, Content } = Layout

const DEFAULT_STANDARD_PATH = '/standard/ASNPO/ns/ns.1'

const MainViewFrameComponent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [selectedStandardId, setSelectedStandardId] = useRecoilState(selectedStandardIdAtom)

  const indexItems = useRecoilValue(indexItemsSelector)
  const selectedOneDepthIndexItem = useRecoilValue(selectedOneDepthIndexItemAtom)
  const setSelectedOneDepthIndexItem = useSetRecoilState(selectedOneDepthIndexItemAtom)
  const setSelectedIndexItem = useSetRecoilState(selectedIndexItemAtom)

  const suspenseFallback = <Spin />

  const setLinkSelectedIndexItem = async (
    standardId: string,
    oneDepthIndexItemId: string,
    indexItemId: string
  ) => {
    const indexList = (await getIndex(standardId)).indexList
    if (isEmpty(indexList)) {
      return false
    }

    const oneDepthIndexItem = await findIndexItemInner(indexList, [oneDepthIndexItemId])
    if (!oneDepthIndexItem) {
      return false
    }

    const indexItem = await findIndexItemInner(
      oneDepthIndexItem.children,
      _.split(indexItemId, '.')
    )

    if (!indexItem) {
      return false
    }

    setSelectedStandardId(standardId)
    setSelectedOneDepthIndexItem(oneDepthIndexItem)
    setSelectedIndexItem(indexItem)

    return true
  }

  useEffect(() => {
    if (location.pathname !== '/standard/link') {
      return
    }

    const linkStandardId = location.state?.linkStandardId
    const linkOneDepthIndexItemId = location.state?.linkOneDepthIndexItemId
    const linkIndexItemId = location.state?.linkIndexItemId
    const linkItemName = location.state?.linkItemName
    if (linkStandardId && linkOneDepthIndexItemId && linkIndexItemId) {
      setLinkSelectedIndexItem(linkStandardId, linkOneDepthIndexItemId, linkIndexItemId).then(
        (isSuccess) => {
          if (!isSuccess) {
            return
          }

          navigate(
            [
              '',
              'standard',
              linkStandardId,
              linkOneDepthIndexItemId,
              linkIndexItemId,
              linkItemName
            ].join('/')
          )
        }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    if (location.pathname === '/standard/link') {
      return
    }

    // URL에서 standardId, oneDepthId, indexId 추출
    const pathParts = location.pathname.split('/').filter(Boolean)

    if (pathParts.length >= 4 && pathParts[0] === 'standard') {
      const [, standardId, oneDepthId, indexId] = pathParts
      // 상태 복원 시도, 실패하면 기본 경로로 이동
      setLinkSelectedIndexItem(standardId, oneDepthId, indexId).then((isSuccess) => {
        if (!isSuccess) {
          navigate(DEFAULT_STANDARD_PATH, { replace: true })
        }
      })
    } else {
      // 잘못된 경로면 기본 경로로 이동
      navigate(DEFAULT_STANDARD_PATH, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const makeIndexListHeader = () => {
    if (isEmpty(indexItems.indexList)) {
      return null
    }

    return (
      <div className="main-view-header-index-list-frame">
        {_.map(indexItems.indexList, (indexItem) => {
          const isSelected = selectedOneDepthIndexItem?.id === indexItem.id
          return (
            <div
              className={classNames('main-view-header-index-item', {
                'main-view-header-index-item-selected': isSelected
              })}
              key={indexItem.id}
              onClick={() => {
                setSelectedOneDepthIndexItem(indexItem)
                const firstChild = findFirstChildIndexItem(indexItem)
                setSelectedIndexItem(firstChild)
                navigate(`/standard/${selectedStandardId}/${indexItem.id}/${firstChild.id}`)
              }}
            >
              <span className="main-view-header-index-item-name">{indexItem.name}</span>
              <NewSearchKeywordCountListComponent
                childrenOverlap
                indexItemId={indexItem.id}
                itemArea={SearchKeywordCountItemAreaType.DROPDOWN_ONE_DEPTH}
              />
            </div>
          )
        })}
      </div>
    )
  }

  const makeMainRightArea = () => {
    return (
      <>
        {makeIndexListHeader()}
        <div className="main-view-right-body-frame">
          <NewBodyComponent />
        </div>
      </>
    )
  }

  const makeLeftSider = () => {
    return (
      <div className="main-view-left-sider-inner-frame">
        <div className="main-view-left-header-frame">
          <div>
            비영리 <span className="main-view-left-header-title">DB</span>
          </div>
        </div>
        <div className="main-view-left-body-frame">
          <Suspense fallback={suspenseFallback}>
            <NewLnbComponent />
          </Suspense>
        </div>
        <div className="main-view-left-footer-frame">청년공인회계사회</div>
      </div>
    )
  }

  return (
    <div className="main-view-wrapper-frame">
      <div className="main-view-frame">
        <Layout>
          <Sider
            collapsible
            className="main-view-left-frame"
            collapsedWidth={70}
            trigger={null}
            width={278}
          >
            {makeLeftSider()}
          </Sider>
          <Content className="main-view-right-frame">
            <div className="main-view-right-content-inner-frame">
              <Suspense fallback={suspenseFallback}>
                <div className="main-view-right-scroll-frame">{makeMainRightArea()}</div>
              </Suspense>
            </div>
          </Content>
        </Layout>
      </div>
    </div>
  )
}

export default MainViewFrameComponent
